import { cn } from '@/lib/utils';
import { File01Icon, Folder01Icon, Folder02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { DownloadSolid } from '@mynaui/icons-react';
import { AlertTriangle, CheckCircle, ChevronDown, Loader2, X } from 'lucide-react';
import prettyBytes from 'pretty-bytes';
import React, { useState } from 'react';

export interface ByproductItem {
  id: string;
  filename: string;
  type: string;
  contentLength?: number | null;
  downloadUrl: string;
}

export interface FileUploadItem {
  id: string;
  name: string;
  path: string;
  size: number;
  type: string;
  downloadUrl?: string;
  byproducts?: ByproductItem[];
  file: File;
  isDirectory: boolean;
  progress?: number;
  status?: 'pending' | 'uploading' | 'completed' | 'failed' | 'cancelled';
  error?: string;
  uploadedBytes?: number;
}

interface FileListProps {
  files: FileUploadItem[];
  onRemoveFile?: (id: string) => void;
  onClearAll?: () => void;
}

interface TreeNode {
  name: string;
  path: string;
  isDirectory: boolean;
  children: Map<string, TreeNode>;
  file?: FileUploadItem;
}

const countAllFiles = (node: TreeNode): number => {
  let count = 0;
  for (const child of node.children.values()) {
    count += child.isDirectory ? countAllFiles(child) : 1;
  }
  return count;
};

const calculateTotalSize = (node: TreeNode): number => {
  let totalSize = 0;
  for (const child of node.children.values()) {
    totalSize += child.isDirectory ? calculateTotalSize(child) : (child.file?.size ?? 0);
  }
  return totalSize;
};

const calculateUploadedBytes = (node: TreeNode): number => {
  let bytes = 0;
  for (const child of node.children.values()) {
    bytes += child.isDirectory ? calculateUploadedBytes(child) : (child.file?.uploadedBytes ?? 0);
  }
  return bytes;
};

const calculateFolderProgress = (node: TreeNode): number => {
  const totalSize = calculateTotalSize(node);
  if (totalSize === 0) return 0;
  return (calculateUploadedBytes(node) / totalSize) * 100;
};

type FolderStatus = 'pending' | 'uploading' | 'completed' | 'failed';

const getFolderStatus = (node: TreeNode): FolderStatus => {
  const files = getAllFiles(node);
  if (files.length === 0) return 'pending';

  const allPending = files.every((f) => !f.status || f.status === 'pending');
  if (allPending) return 'pending';

  const anyUploading = files.some((f) => f.status === 'uploading');
  if (anyUploading) return 'uploading';

  const allCompleted = files.every((f) => f.status === 'completed');
  if (allCompleted) return 'completed';

  const anyFailed = files.some((f) => f.status === 'failed' || f.status === 'cancelled');
  if (anyFailed) return 'failed';

  return 'uploading';
};

const getAllFiles = (node: TreeNode): FileUploadItem[] => {
  const files: FileUploadItem[] = [];
  for (const child of node.children.values()) {
    if (child.isDirectory) {
      files.push(...getAllFiles(child));
    } else if (child.file) {
      files.push(child.file);
    }
  }
  return files;
};

const getAllFileIds = (node: TreeNode): string[] => {
  return getAllFiles(node).map((f) => f.id);
};

const Node: React.FC<{
  node: TreeNode;
  depth: number;
  onRemoveFile?: (id: string) => void;
}> = ({ node, depth, onRemoveFile }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!node.isDirectory) {
    const file = node.file!;
    const canRemove = !file.status || file.status === 'pending';
    const isUploading = file.status === 'uploading';

    return (
      <div className={cn('flex items-center gap-2 text-sm font-medium p-1 relative z-[2]')}>
        {isUploading && (
          <div
            className='absolute inset-0 rounded-sm bg-gradient-to-r from-green-500/5 to-green-500/20 -z-10'
            style={{ width: `${file.progress || 0}%` }}
          />
        )}
        <HugeiconsIcon
          icon={File01Icon}
          className='size-5 text-gray-500 fill-gray-100 dark:fill-stone-700 dark:text-stone-300 flex-shrink-0'
        />
        <span className='flex-1 truncate'>{node.name}</span>
        <div className='flex items-center gap-2'>
          {file.byproducts?.map((bp) => (
            <button
              key={bp.id}
              className={cn(
                'flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded',
                'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
                'dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50',
                'cursor-pointer transition-colors',
              )}
              onClick={() => window.open(bp.downloadUrl, '_blank')}
              title={`Download ${bp.filename}`}>
              <DownloadSolid className='size-3' />
              {bp.type}
            </button>
          ))}
          {file.downloadUrl && (
            <span title='Download original file'>
              <DownloadSolid
                className='size-5 text-gray-500 fill-gray-100 dark:fill-stone-700 dark:text-stone-300 cursor-pointer'
                onClick={() => window.open(file.downloadUrl, '_blank')}
              />
            </span>
          )}
          <span className='text-xs text-gray-500 dark:text-stone-400 font-medium'>
            {isUploading
              ? `${prettyBytes(file.uploadedBytes || 0)} / ${prettyBytes(file.size)}`
              : prettyBytes(file.size)}
          </span>
          {onRemoveFile && (
            <div className='flex items-center justify-center'>
              {canRemove ? (
                <button
                  onClick={() => onRemoveFile(file.id)}
                  className='transition-opacity p-1 rounded hover:bg-red-100 text-red-600 flex items-center justify-center'
                  title='Remove file'>
                  <X className='size-3' />
                </button>
              ) : (
                <div
                  className={cn(
                    'p-1 rounded flex items-center justify-center',
                    file.status === 'completed' && 'bg-green-50',
                    (file.status === 'failed' || file.status === 'cancelled') && 'bg-red-50',
                  )}
                  title={
                    file.status === 'uploading'
                      ? `Uploading... ${Math.round(file.progress || 0)}%`
                      : file.status === 'completed'
                        ? 'Upload completed'
                        : `Upload failed: ${file.error || 'Unknown error'}`
                  }>
                  {file.status === 'uploading' && (
                    <Loader2 className='size-3 animate-spin text-slate-500 stroke-current stroke-2' />
                  )}
                  {file.status === 'completed' && <CheckCircle className='size-3 text-green-600 stroke-2' />}
                  {(file.status === 'failed' || file.status === 'cancelled') && (
                    <AlertTriangle className='size-3 text-red-600 stroke-2' />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Directory node
  const folderStatus = getFolderStatus(node);
  const canRemove = folderStatus === 'pending';
  const isUploading = folderStatus === 'uploading';

  const handleRemoveFolder = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemoveFile) {
      getAllFileIds(node).forEach((id) => onRemoveFile(id));
    }
  };

  return (
    <>
      <div
        className='flex items-center gap-2 text-sm font-medium p-1 cursor-pointer group relative z-[2]'
        onClick={() => setIsExpanded((prev) => !prev)}>
        {isUploading && (
          <div
            className='absolute inset-0 rounded-sm bg-gradient-to-r from-green-500/5 to-green-500/20 -z-10'
            style={{ width: `${calculateFolderProgress(node)}%` }}
          />
        )}
        <ChevronDown className={cn('size-4 transition-transform flex-shrink-0', !isExpanded && '-rotate-90')} />
        <HugeiconsIcon
          icon={isExpanded ? Folder02Icon : Folder01Icon}
          className='size-5 text-gray-500 fill-gray-100 dark:fill-stone-700 dark:text-stone-300'
        />
        <span className='flex-1 truncate group-hover:underline'>{node.name}</span>
        <div className='flex items-center gap-2'>
          <span className='text-xs text-gray-500 dark:text-stone-400 font-medium'>
            {isUploading
              ? `${prettyBytes(calculateUploadedBytes(node))} / ${prettyBytes(calculateTotalSize(node))} (${Math.round(calculateFolderProgress(node))}%)`
              : `${countAllFiles(node)} files`}
          </span>
          {onRemoveFile && (
            <div className='flex items-center justify-center'>
              {canRemove ? (
                <button
                  onClick={handleRemoveFolder}
                  className='transition-opacity p-1 rounded hover:bg-red-100 text-red-600 flex items-center justify-center'
                  title='Remove folder'>
                  <X className='size-3' />
                </button>
              ) : (
                <div
                  className={cn(
                    'p-1 rounded flex items-center justify-center',
                    folderStatus === 'completed' && 'bg-green-50',
                    folderStatus === 'failed' && 'bg-red-50',
                  )}
                  title={
                    folderStatus === 'uploading'
                      ? `Uploading folder... ${Math.round(calculateFolderProgress(node))}%`
                      : folderStatus === 'completed'
                        ? 'Upload completed'
                        : 'Upload failed'
                  }>
                  {folderStatus === 'uploading' && (
                    <Loader2 className='size-3 animate-spin text-slate-500 stroke-current stroke-2' />
                  )}
                  {folderStatus === 'completed' && <CheckCircle className='size-3 text-green-600 stroke-2' />}
                  {folderStatus === 'failed' && <AlertTriangle className='size-3 text-red-600 stroke-2' />}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className='pl-7 relative'>
          <div className='absolute left-3.5 -translate-x-1/2 top-0 bottom-0 w-px bg-gray-200 dark:bg-stone-800' />
          <div className='flex flex-col gap-1'>
            {Array.from(node.children.entries()).map(([nodeName, child]) => (
              <Node key={nodeName} node={child} depth={depth + 1} onRemoveFile={onRemoveFile} />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

const buildTree = (files: FileUploadItem[]): TreeNode => {
  const root: TreeNode = { name: 'Root', path: '', isDirectory: true, children: new Map() };

  files.forEach((file) => {
    const pathParts = file.path.split('/').filter((part) => part.length > 0);
    let currentNode = root;

    for (let i = 0; i < pathParts.length - 1; i++) {
      const dirName = pathParts[i];
      if (!currentNode.children.has(dirName)) {
        currentNode.children.set(dirName, {
          name: dirName,
          path: pathParts.slice(0, i + 1).join('/'),
          isDirectory: true,
          children: new Map(),
        });
      }
      currentNode = currentNode.children.get(dirName)!;
    }

    const fileName = pathParts[pathParts.length - 1] || file.name;
    currentNode.children.set(fileName, {
      name: fileName,
      path: file.path,
      isDirectory: false,
      children: new Map(),
      file,
    });
  });

  return root;
};

const FileList: React.FC<FileListProps> = ({ files, onRemoveFile }) => {
  if (files.length === 0) return null;

  const treeRoot = buildTree(files);

  return (
    <div className='flex flex-col gap-1 text-left select-none'>
      {Array.from(treeRoot.children.entries())
        .sort(([, a], [, b]) => {
          if (a.isDirectory && !b.isDirectory) return -1;
          if (!a.isDirectory && b.isDirectory) return 1;
          return a.name.localeCompare(b.name);
        })
        .map(([nodeName, node]) => (
          <Node key={nodeName} node={node} depth={0} onRemoveFile={onRemoveFile} />
        ))}
    </div>
  );
};

export default FileList;
